import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Store, LogOut, Camera, StopCircle, ScanLine, CheckCircle2, MapPin } from "lucide-react";
import { Html5Qrcode, Html5QrcodeScannerState } from "html5-qrcode";
import { format } from "date-fns";

const statusColor = (s) => ({
  paid: "bg-pink-500/20 text-pink-300 border-pink-500/30",
  pending: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  rejected: "bg-red-500/20 text-red-300 border-red-500/30",
  refunded: "bg-red-500/20 text-red-300 border-red-500/30",
}[s] || "bg-slate-500/20 text-slate-300 border-slate-500/30");

// Shop-side vocabulary: pending payout, settled payout, or refunded.
const shopLabel = (status) => {
  if (status === "pending") return "Payment Pending";
  if (status === "paid") return "Paid";
  if (status === "rejected") return "Refunded";
  return status;
};

function CameraScanner({ onDecoded, active, onStop }) {
  const containerId = "shop-qr-reader";
  const scannerRef = useRef(null);
  const startedRef = useRef(false);
  const decodedRef = useRef(false);
  const [status, setStatus] = useState("checking"); // checking | ready | denied | notfound | error | unsupported

  const safeStop = async () => {
    const s = scannerRef.current;
    if (!s || !startedRef.current) return;
    try {
      const state = s.getState?.();
      if (state === Html5QrcodeScannerState.SCANNING || state === Html5QrcodeScannerState.PAUSED) {
        await s.stop();
      }
    } catch (e) { console.debug("scanner stop skipped:", e?.message); }
    try { s.clear(); } catch (e) { console.debug("scanner clear skipped:", e?.message); }
    startedRef.current = false;
  };

  useEffect(() => {
    if (!active) return undefined;
    let disposed = false;

    (async () => {
      // 1. Preflight — do we have secure context + mediaDevices?
      if (!window.isSecureContext) {
        setStatus("unsupported");
        return;
      }
      if (!navigator.mediaDevices?.getUserMedia) {
        setStatus("unsupported");
        return;
      }
      // 2. Probe permission with a short-lived stream; iOS Safari requires this to prompt.
      let probeStream = null;
      try {
        probeStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
          audio: false,
        });
      } catch (err) {
        if (disposed) return;
        const name = err?.name || "";
        if (name === "NotAllowedError" || name === "SecurityError") setStatus("denied");
        else if (name === "NotFoundError" || name === "OverconstrainedError") setStatus("notfound");
        else setStatus("error");
        return;
      }
      // Release probe tracks; html5-qrcode will re-acquire.
      probeStream.getTracks().forEach((t) => t.stop());
      if (disposed) return;

      // 3. Start html5-qrcode inside the container.
      const el = document.getElementById(containerId);
      if (!el) return;
      const scanner = new Html5Qrcode(containerId, { verbose: false });
      scannerRef.current = scanner;
      try {
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 220, height: 220 }, aspectRatio: 1 },
          (decoded) => {
            if (disposed || decodedRef.current) return;
            decodedRef.current = true;
            safeStop();
            onDecoded?.(decoded);
          },
          () => {},
        );
        startedRef.current = true;
        // iOS Safari fix: force playsinline on the auto-injected <video>.
        el.querySelectorAll("video").forEach((v) => {
          v.setAttribute("playsinline", "true");
          v.setAttribute("muted", "true");
          v.muted = true;
        });
        setStatus("ready");
      } catch (e) {
        if (disposed) return;
        setStatus("error");
      }
    })();

    return () => {
      disposed = true;
      safeStop();
    };
  }, [active, onDecoded]);

  if (!active) return null;
  return (
    <div className="space-y-3">
      <div id={containerId} data-testid="shop-camera" className="rounded-lg overflow-hidden bg-black w-full max-w-sm mx-auto aspect-square" />
      {status === "checking" && <div className="text-xs text-slate-400 text-center">Requesting camera permission…</div>}
      {status === "denied" && (
        <div className="velo-surface rounded-lg p-3 text-xs text-amber-300">
          Camera permission denied. On iOS: Safari → aA icon → Website Settings → Camera → Allow, then tap Start camera again.
          Or use the manual code entry on the right.
        </div>
      )}
      {status === "notfound" && (
        <div className="velo-surface rounded-lg p-3 text-xs text-amber-300">No camera detected on this device. Please enter the code manually.</div>
      )}
      {status === "unsupported" && (
        <div className="velo-surface rounded-lg p-3 text-xs text-amber-300">Camera access requires HTTPS on this browser. Please enter the code manually.</div>
      )}
      {status === "error" && (
        <div className="velo-surface rounded-lg p-3 text-xs text-amber-300">Couldn't start the camera. Please try again or enter the code manually.</div>
      )}
    </div>
  );
}

function ScanResultCard({ result, onDismiss }) {
  if (!result) return null;
  return (
    <div data-testid="scan-result" className="velo-card p-5 border-2 border-emerald-500/40">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0">
          <CheckCircle2 className="w-5 h-5 text-emerald-400" strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-white font-medium">Redemption recorded</div>
          <div className="text-xs text-slate-400 mt-0.5">Sent to accountant as <Badge className={`ml-1 text-[10px] border ${statusColor(result.status)}`}>{shopLabel(result.status)}</Badge></div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div className="velo-surface p-3 rounded">
              <div className="text-[10px] text-slate-500 uppercase tracking-widest">Customer</div>
              <div className="text-white text-sm mt-1">{result.user_name}</div>
              {result.user_phone ? <div className="text-slate-500 font-mono text-xs">{result.user_phone}</div> : null}
            </div>
            <div className="velo-surface p-3 rounded">
              <div className="text-[10px] text-slate-500 uppercase tracking-widest">Amount</div>
              <div className="text-emerald-300 text-sm mt-1 font-mono">₹{Number(result.amount).toLocaleString("en-IN")}</div>
              <div className="text-slate-500 font-mono text-xs">{result.points.toLocaleString("en-IN")} pts</div>
            </div>
          </div>
          <div className="mt-3 text-[10px] text-slate-500 uppercase tracking-widest">Code · <span className="font-mono text-pink-400">{result.redemption_code}</span></div>
        </div>
      </div>
      <Button onClick={onDismiss} className="mt-4 w-full rounded-full bg-pink-500 hover:bg-pink-400 text-black" data-testid="btn-dismiss-scan">Scan another</Button>
    </div>
  );
}

export default function ShopDashboard() {
  const { shop, logout } = useAuth();
  const [manualCode, setManualCode] = useState("");
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(() => {
    api.get("/shop/redemptions").then((r) => setHistory(r.data)).catch((e) => console.debug("shop history poll failed:", e?.message));
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const submit = useCallback(async ({ token, code }) => {
    setBusy(true);
    try {
      const raw = (code || token || "").trim();
      // If scanned/typed value is a short redemption code, use the code path;
      // otherwise treat it as a signed QR token.
      const isShortCode = /^[A-Z0-9]{6,12}$/i.test(raw);
      const payload = isShortCode ? { code: raw.toUpperCase() } : { token: raw };
      const { data } = await api.post("/shop/scan", payload);
      setResult(data);
      setScanning(false);
      setManualCode("");
      toast.success(`Redemption ₹${data.amount} recorded`);
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scan failed");
    } finally { setBusy(false); }
  }, [loadHistory]);

  const onDecoded = useCallback((text) => submit({ token: text }), [submit]);
  const submitManual = () => {
    const c = manualCode.trim().toUpperCase();
    if (!c) return toast.error("Enter a code");
    submit({ code: c });
  };

  return (
    <div className="min-h-screen bg-[#150733] noise-bg">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#170929]/85 border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
            <img src="/veloskin-logo.png" alt="Veloskin Agility Club" className="h-14 w-auto drop-shadow-[0_0_18px_rgba(240,21,79,0.35)]" />
            <div className="min-w-0">
              <div className="text-white text-sm font-semibold tracking-tight truncate">Veloskin Agility Club</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-[0.2em]">Shop portal</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-sm text-white font-medium truncate max-w-[180px]">{shop?.name}</div>
              <div className="text-xs text-slate-500 font-mono">{shop?.id}</div>
            </div>
            <Button data-testid="btn-shop-logout" onClick={logout} variant="outline" size="sm" className="rounded-full border-white/15 bg-transparent text-slate-300 hover:bg-white/5">
              <LogOut className="w-4 h-4 sm:mr-2" strokeWidth={1.5} /><span className="hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl text-white font-light tracking-tight">
            Scan a customer's redemption
          </h1>
          <p className="text-slate-400 mt-1 text-sm flex items-center gap-1.5">
            <Store className="w-3.5 h-3.5 text-pink-400" />{shop?.name}
            {shop?.location ? <span className="text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3" />{shop.location}</span> : null}
          </p>
        </div>

        {result ? (
          <ScanResultCard result={result} onDismiss={() => setResult(null)} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="velo-card p-5 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-slate-500">Option A</div>
                  <div className="text-white text-lg font-medium mt-0.5">Scan QR</div>
                </div>
                {scanning ? (
                  <Button size="sm" variant="outline" onClick={() => setScanning(false)} data-testid="btn-stop-camera" className="rounded-full border-white/15 bg-transparent text-slate-300 hover:bg-white/5"><StopCircle className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} />Stop</Button>
                ) : (
                  <Button size="sm" onClick={() => setScanning(true)} data-testid="btn-start-camera" className="rounded-full bg-pink-500 hover:bg-pink-400 text-black"><Camera className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} />Start camera</Button>
                )}
              </div>
              {scanning ? (
                <CameraScanner active={scanning} onDecoded={onDecoded} onStop={() => setScanning(false)} />
              ) : (
                <div className="velo-surface rounded-lg p-8 text-center">
                  <ScanLine className="w-8 h-8 text-slate-600 mx-auto mb-3" strokeWidth={1.5} />
                  <div className="text-sm text-slate-400">Ask the customer to open their dashboard and show the QR.</div>
                </div>
              )}
            </div>

            <div className="velo-card p-5 sm:p-6">
              <div className="mb-4">
                <div className="text-[11px] uppercase tracking-widest text-slate-500">Option B</div>
                <div className="text-white text-lg font-medium mt-0.5">Enter code manually</div>
              </div>
              <Label className="text-slate-300 text-xs uppercase tracking-widest">Redemption code</Label>
              <Input
                data-testid="input-manual-code"
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value.toUpperCase())}
                placeholder="e.g. 7HK4P2QR"
                className="velo-input mt-2 h-12 font-mono tracking-[0.2em] text-lg"
                maxLength={12}
              />
              <Button data-testid="btn-submit-manual" onClick={submitManual} disabled={busy || !manualCode.trim()} className="mt-4 w-full h-11 rounded-full bg-pink-500 hover:bg-pink-400 text-black">
                {busy ? "Validating…" : "Validate & record"}
              </Button>
              <div className="mt-3 text-[11px] text-slate-500">Codes are valid for 10 minutes only.</div>
            </div>
          </div>
        )}

        <div className="velo-card p-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-white text-lg font-medium">Your recent redemptions</div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 mt-0.5">Awaiting accountant approval + settled</div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px]">
              <thead className="bg-[#170929] border-b border-white/10">
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Code</th>
                  <th className="px-4 py-3 text-right">Amount</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">When</th>
                </tr>
              </thead>
              <tbody>
                {history.map((r) => (
                  <tr key={r.id} data-testid={`shop-history-${r.id}`} className="border-b border-white/5">
                    <td className="px-4 py-3 text-sm text-white">{r.user_name}</td>
                    <td className="px-4 py-3 text-xs font-mono text-pink-400">{r.redemption_code}</td>
                    <td className="px-4 py-3 text-sm text-right text-emerald-300 font-mono">₹{Number(r.amount).toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3"><Badge className={`border capitalize text-[10px] ${statusColor(r.status)}`}>{shopLabel(r.status)}</Badge></td>
                    <td className="px-4 py-3 text-xs text-slate-400">{format(new Date(r.created_at), "dd MMM, HH:mm")}</td>
                  </tr>
                ))}
                {history.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-500 text-sm">No redemptions yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
